import React, { useEffect, useCallback, memo } from "react";
import { useFormContext } from "react-hook-form";
import { useInView } from "react-intersection-observer";

import { HookFormRadioButton } from "components";
import { PaymentMethodValues, PaymentTypes } from "types";

import AchLabel from "./achLabel";
import CreditCardLabel from "./creditCardLabel";

import styles from "./paymentMethodsDdl.module.scss";

type PaymentMethodsDdlProps = {
	paymentMethods?: {
		pages: {
			items: PaymentMethodValues[];
			count: number;
		}[];
	};
	paymentMethodName: string;
	fetchNextPage: Function;
	isFetchingNextPage?: boolean;
	hasNextPage?: boolean;
};

const PaymentMethodsDdl = ({
	paymentMethods,
	paymentMethodName,
	fetchNextPage,
	isFetchingNextPage,
	hasNextPage,
}: PaymentMethodsDdlProps) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		delay: 500,
	});
	const { register, trigger, setValue } = useFormContext();

	useEffect(() => {
		if (inView) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView]);

	const handlePaymentMethodChange = useCallback(
		e => {
			setValue(`${paymentMethodName}Id`, e?.target?.value);
			setValue(
				`${paymentMethodName}Data`,
				paymentMethods?.pages?.[0]?.items?.find(
					item => item?.id === e?.target?.value,
				),
			);
			trigger(`${paymentMethodName}Id`);
		},
		[paymentMethodName, paymentMethods?.pages, setValue, trigger],
	);

	return (
		<ul className={styles.wrapper}>
			{paymentMethods?.pages?.map((page, index) => (
				<React.Fragment key={index}>
					{page?.items?.map((paymentMethod: PaymentMethodValues) => {
						return (
							<li key={paymentMethod?.id}>
								<label className={styles.item}>
									<div className={styles.radiobutton}>
										<HookFormRadioButton
											id={paymentMethod?.id}
											name={paymentMethodName}
											register={register}
											value={paymentMethod?.id}
											onChange={handlePaymentMethodChange}
										/>
									</div>
									{paymentMethod?.paymentType?.name ===
									PaymentTypes.ach.name ? (
										<AchLabel data={paymentMethod} />
									) : (
										<CreditCardLabel data={paymentMethod} />
									)}
								</label>
							</li>
						);
					})}
				</React.Fragment>
			))}
			<li ref={ref} className={styles.loading}>
				{isFetchingNextPage && hasNextPage && "Loading..."}
			</li>
		</ul>
	);
};

export default memo(PaymentMethodsDdl);
